import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { ArrowLeft, FileSignature, Calendar, DollarSign, User, Trash2, ExternalLink, RefreshCw, Check, Clock, AlertTriangle, FileText, Download, Edit2, X, Upload, CheckCircle, AlertCircle, Receipt } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  const fileInputRef = useRef(null);
  const boletoInputRefs = useRef({});
  
  const [contract, setContract] = useState(null);
  const [contractDocuments, setContractDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingInstallment, setUpdatingInstallment] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingDueDates, setEditingDueDates] = useState(false);
  const [editedDueDates, setEditedDueDates] = useState([]);
  const [savingDueDates, setSavingDueDates] = useState(false);
  
  // Description edit states
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  
  // Document upload states
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [docDescription, setDocDescription] = useState('');
  
  // Boleto upload states
  const [uploadingBoleto, setUploadingBoleto] = useState(null);

  // Contract file download handler
  const handleContractFileDownload = async () => {
    try {
      // Use current origin to ensure correct URL in both production and staging
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}${contract.contract_file}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', contract.contract_filename || 'contrato.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract file:', error);
      alert('Erro ao baixar arquivo do contrato');
    }
  };

  // Helper function to format date without timezone issues
  const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  useEffect(() => {
    fetchContract();
    fetchContractDocuments();
  }, [id]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/admin/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContract(response.data);
    } catch (error) {
      console.error('Error fetching contract:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContractDocuments = async () => {
    try {
      const response = await axios.get(`${API}/admin/documents/contract/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractDocuments(response.data);
    } catch (error) {
      console.error('Error fetching contract documents:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', docDescription);

    try {
      await axios.post(
        `${API}/admin/documents/upload/contract/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadMessage({ type: 'success', text: 'Documento enviado com sucesso!' });
      setDocDescription('');
      setShowUploadForm(false);
      fetchContractDocuments();
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar documento';
      setUploadMessage({ type: 'error', text: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId, filename) => {
    if (!window.confirm(`Excluir o documento "${filename}"?`)) return;

    try {
      await axios.delete(`${API}/admin/documents/admin/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContractDocuments();
    } catch (error) {
      alert('Erro ao excluir documento');
    }
  };

  // Document download handler
  const handleDocumentDownload = async (doc) => {
    try {
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}${doc.file_url}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Erro ao baixar documento');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir este contrato?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/admin/contracts');
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Erro ao excluir contrato');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/admin/contracts/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContract({ ...contract, status: newStatus });
      setShowStatusModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleInstallmentPayment = async (index, newStatus) => {
    setUpdatingInstallment(index);
    try {
      await axios.put(
        `${API}/admin/contracts/${id}/installment/${index}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state immediately
      setContract(prevContract => {
        const updatedPayments = [...(prevContract.installment_payments || [])];
        while (updatedPayments.length < prevContract.installments) {
          updatedPayments.push('pending');
        }
        updatedPayments[index] = newStatus;
        return { ...prevContract, installment_payments: updatedPayments };
      });
      
      // Show success feedback
      const statusLabel = newStatus === 'paid' ? 'Pago' : 'Pendente';
      console.log(`Parcela ${index + 1} marcada como ${statusLabel}`);
    } catch (error) {
      console.error('Error updating installment:', error);
      alert('Erro ao atualizar parcela');
    } finally {
      setUpdatingInstallment(null);
    }
  };

  // Boleto upload handler
  const handleBoletoUpload = async (index, file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Apenas arquivos PDF são permitidos');
      return;
    }
    
    setUploadingBoleto(index);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/admin/contracts/${id}/installments/${index}/boleto`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      // Refresh contract data
      await fetchContract();
      alert('Boleto enviado com sucesso!');
    } catch (error) {
      console.error('Error uploading boleto:', error);
      alert(error.response?.data?.detail || 'Erro ao enviar boleto');
    } finally {
      setUploadingBoleto(null);
      // Clear file input
      if (boletoInputRefs.current[index]) {
        boletoInputRefs.current[index].value = '';
      }
    }
  };

  // Boleto download handler
  // Boleto download handler
  const handleBoletoDownload = async (index) => {
    try {
      // Use current origin to ensure correct URL in both production and staging
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}/api/admin/contracts/${id}/installments/${index}/boleto`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const boleto = contract.installment_boletos?.[index];
      link.setAttribute('download', boleto?.original_name || `boleto_parcela_${index + 1}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading boleto:', error);
      alert('Erro ao baixar boleto');
    }
  };

  // Boleto delete handler
  const handleBoletoDelete = async (index) => {
    if (!confirm(`Tem certeza que deseja remover o boleto da parcela ${index + 1}?`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/admin/contracts/${id}/installments/${index}/boleto`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh contract data
      await fetchContract();
      alert('Boleto removido com sucesso!');
    } catch (error) {
      console.error('Error deleting boleto:', error);
      alert(error.response?.data?.detail || 'Erro ao remover boleto');
    }
  };

  const handleEditDueDates = () => {
    setEditedDueDates([...(contract.due_dates || [])]);
    setEditingDueDates(true);
  };

  const handleDueDateChange = (index, newDate) => {
    const updated = [...editedDueDates];
    updated[index] = newDate;
    setEditedDueDates(updated);
  };

  const handleSaveDueDates = async () => {
    setSavingDueDates(true);
    try {
      await axios.put(
        `${API}/admin/contracts/${id}`,
        { due_dates: editedDueDates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContract({ ...contract, due_dates: editedDueDates });
      setEditingDueDates(false);
    } catch (error) {
      console.error('Error updating due dates:', error);
      alert('Erro ao atualizar datas de vencimento');
    } finally {
      setSavingDueDates(false);
    }
  };

  const handleCancelEditDueDates = () => {
    setEditingDueDates(false);
    setEditedDueDates([]);
  };

  // Description edit functions
  const handleEditDescription = () => {
    setEditedDescription(contract.description || '');
    setEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      const baseUrl = window.location.origin;
      await axios.patch(
        `${baseUrl}/api/admin/contracts/${id}`,
        { description: editedDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContract({ ...contract, description: editedDescription });
      setEditingDescription(false);
    } catch (error) {
      console.error('Error saving description:', error);
      alert('Erro ao salvar descrição');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleCancelEditDescription = () => {
    setEditingDescription(false);
    setEditedDescription('');
  };

  // Calculate payment status considering due date
  const getInstallmentPaymentStatus = (index) => {
    const payments = contract.installment_payments || [];
    const storedStatus = payments[index] || 'pending';
    
    // If already paid or cancelled, return as-is
    if (storedStatus === 'paid') return 'paid';
    if (storedStatus === 'cancelled') return 'cancelled';
    
    // If contract is cancelled, unpaid installments are cancelled
    if (contract.status === 'cancelled') return 'cancelled';
    
    // Check if overdue (past due date and not paid)
    const dueDate = contract.due_dates?.[index];
    if (dueDate && storedStatus !== 'paid') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Parse date string directly to avoid timezone issues
      const parts = dueDate.split('-');
      const dueDateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      dueDateObj.setHours(0, 0, 0, 0);
      
      if (today > dueDateObj) {
        return 'overdue';
      }
    }
    
    return storedStatus;
  };

  const getPaymentStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'Pendente',
        color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
        icon: Clock,
        bgColor: 'bg-yellow-50'
      },
      paid: {
        label: 'Pago',
        color: 'bg-green-100 text-green-700 border-green-300',
        icon: Check,
        bgColor: 'bg-green-50'
      },
      overdue: {
        label: 'Em Atraso',
        color: 'bg-red-100 text-red-700 border-red-300',
        icon: AlertTriangle,
        bgColor: 'bg-red-50'
      },
      cancelled: {
        label: 'Cancelada',
        color: 'bg-gray-100 text-gray-400 border-gray-300',
        icon: AlertTriangle,
        bgColor: 'bg-gray-50'
      }
    };
    return configs[status] || configs.pending;
  };

  const statusOptions = [
    { value: 'pending', label: 'Pendente', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', description: 'Aguardando assinatura ou confirmação' },
    { value: 'active', label: 'Ativo', color: 'bg-green-100 text-green-700 border-green-300', description: 'Contrato em vigor' },
    { value: 'inactive', label: 'Inativo', color: 'bg-gray-100 text-gray-700 border-gray-300', description: 'Contrato suspenso temporariamente' },
    { value: 'judicialized', label: 'Judicializado', color: 'bg-red-100 text-red-700 border-red-300', description: 'Contrato em processo judicial' },
    { value: 'completed', label: 'Concluído', color: 'bg-blue-100 text-blue-700 border-blue-300', description: 'Contrato finalizado com sucesso' },
    { value: 'cancelled', label: 'Cancelado', color: 'bg-red-100 text-red-600 border-red-300', description: 'Contrato cancelado' }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(s => s.value === status) || { 
      value: status, 
      label: status, 
      color: 'bg-gray-100 text-gray-700 border-gray-300',
      description: ''
    };
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Calculate totals
  const calculateTotals = () => {
    let paidCount = 0;
    let overdueCount = 0;
    let pendingCount = 0;
    let cancelledCount = 0;
    let paidAmount = 0;

    for (let i = 0; i < (contract?.installments || 0); i++) {
      const status = getInstallmentPaymentStatus(i);
      if (status === 'paid') {
        paidCount++;
        paidAmount += contract.installment_value || 0;
      } else if (status === 'overdue') {
        overdueCount++;
      } else if (status === 'cancelled') {
        cancelledCount++;
      } else {
        pendingCount++;
      }
    }

    return { paidCount, overdueCount, pendingCount, cancelledCount, paidAmount };
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <FileSignature size={48} className="mx-auto mb-4 text-gray-400" />
        <p className="text-gray-500">Contrato não encontrado</p>
        <button
          onClick={() => navigate('/admin/contracts')}
          className="mt-4 text-arcano-blue hover:underline"
        >
          Voltar para lista
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Alterar Status do Contrato</h2>
            <p className="text-gray-600 mb-4">Selecione o novo status para este contrato:</p>
            
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(option.value)}
                  disabled={updatingStatus || contract.status === option.value}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    contract.status === option.value
                      ? `${option.color} border-current cursor-default`
                      : 'border-gray-200 hover:border-arcano-blue hover:bg-gray-50'
                  } ${updatingStatus ? 'opacity-50 cursor-wait' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-semibold ${contract.status === option.value ? '' : 'text-gray-900'}`}>
                        {option.label}
                      </span>
                      <p className={`text-sm ${contract.status === option.value ? '' : 'text-gray-500'}`}>
                        {option.description}
                      </p>
                    </div>
                    {contract.status === option.value && (
                      <span className="text-xs font-semibold px-2 py-1 bg-white/50 rounded">Atual</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowStatusModal(false)}
                disabled={updatingStatus}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/contracts')}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft size={24} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileSignature className="text-arcano-blue" />
              Detalhes do Contrato
            </h1>
            <p className="text-gray-600">
              {contract.description || `Contrato #${contract.id.substring(0, 8)}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowStatusModal(true)}
            className="flex items-center gap-2 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors"
          >
            <RefreshCw size={18} />
            Alterar Status
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <Trash2 size={18} />
            {deleting ? 'Excluindo...' : 'Excluir'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Contract Info */}
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div className="flex items-center justify-between pb-4 border-b">
            <h2 className="text-xl font-bold text-gray-900">Informações do Contrato</h2>
            <button
              onClick={() => setShowStatusModal(true)}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              title="Clique para alterar o status"
            >
              {getStatusBadge(contract.status)}
            </button>
          </div>

          <div className="space-y-4">
            {/* Descrição do Contrato */}
            <div className="flex items-start gap-3">
              <FileSignature className="text-arcano-blue mt-1" size={20} />
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">Descrição</p>
                {editingDescription ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent text-sm"
                      placeholder="Ex: Desenvolvimento de Sistema Web"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveDescription}
                      disabled={savingDescription}
                      className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      title="Salvar"
                    >
                      {savingDescription ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        <Check size={16} />
                      )}
                    </button>
                    <button
                      onClick={handleCancelEditDescription}
                      className="p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      title="Cancelar"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">
                      {contract.description || <span className="text-gray-400 italic">Sem descrição</span>}
                    </p>
                    <button
                      onClick={handleEditDescription}
                      className="p-1 text-gray-400 hover:text-arcano-blue hover:bg-gray-100 rounded transition-colors"
                      title="Editar descrição"
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <User className="text-arcano-blue" size={20} />
              <div>
                <p className="text-sm text-gray-500">Cliente</p>
                <Link 
                  to={`/admin/clients/${encodeURIComponent(contract.client_email)}`}
                  className="font-semibold text-arcano-blue hover:underline"
                >
                  {contract.client_email}
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <DollarSign className="text-green-600" size={20} />
              <div>
                <p className="text-sm text-gray-500">Valor Total</p>
                <p className="font-semibold text-2xl text-gray-900">
                  R$ {contract.total_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Parcelas</p>
                <p className="font-semibold text-gray-900">{contract.installments}x</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Valor por Parcela</p>
                <p className="font-semibold text-gray-900">
                  R$ {contract.installment_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="text-arcano-blue" size={20} />
              <div>
                <p className="text-sm text-gray-500">Criado em</p>
                <p className="font-semibold text-gray-900">
                  {new Date(contract.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {contract.contract_url && (
              <div className="pt-4 border-t">
                <a
                  href={contract.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-arcano-blue hover:underline"
                >
                  <ExternalLink size={18} />
                  Ver documento do contrato (URL)
                </a>
              </div>
            )}

            {contract.contract_file && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Arquivo do Contrato</p>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-arcano-blue/10 rounded-lg flex items-center justify-center">
                    <FileText className="text-arcano-blue" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{contract.contract_filename || 'Contrato'}</p>
                    <button
                      onClick={handleContractFileDownload}
                      className="text-sm text-arcano-blue hover:underline flex items-center gap-1"
                    >
                      <Download size={14} />
                      Baixar arquivo
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Resumo de Pagamentos</h2>
          
          <div className={`grid ${totals.cancelledCount > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-4 mb-6`}>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <Check className="mx-auto text-green-600 mb-2" size={24} />
              <p className="text-2xl font-bold text-green-700">{totals.paidCount}</p>
              <p className="text-sm text-green-600">Pagas</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <Clock className="mx-auto text-yellow-600 mb-2" size={24} />
              <p className="text-2xl font-bold text-yellow-700">{totals.pendingCount}</p>
              <p className="text-sm text-yellow-600">Pendentes</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <AlertTriangle className="mx-auto text-red-600 mb-2" size={24} />
              <p className="text-2xl font-bold text-red-700">{totals.overdueCount}</p>
              <p className="text-sm text-red-600">Em Atraso</p>
            </div>
            {totals.cancelledCount > 0 && (
              <div className="text-center p-4 bg-gray-100 rounded-lg">
                <AlertTriangle className="mx-auto text-gray-400 mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-400">{totals.cancelledCount}</p>
                <p className="text-sm text-gray-400">Canceladas</p>
              </div>
            )}
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Recebido:</span>
              <span className="text-xl font-bold text-green-600">
                R$ {totals.paidAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-600">Valor Restante:</span>
              <span className="text-xl font-bold text-gray-700">
                R$ {((totals.pendingCount + totals.overdueCount) * (contract.installment_value || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>
            {totals.cancelledCount > 0 && (
              <div className="flex justify-between items-center mt-2">
                <span className="text-gray-400">Valor Cancelado:</span>
                <span className="text-xl font-bold text-gray-400 line-through">
                  R$ {(totals.cancelledCount * (contract.installment_value || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Installments / Due Dates */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Parcelas e Vencimentos</h2>
          {!editingDueDates ? (
            <button
              onClick={handleEditDueDates}
              className="flex items-center gap-2 text-arcano-blue hover:bg-arcano-blue/10 px-3 py-2 rounded-md transition-colors"
            >
              <Edit2 size={18} />
              Editar Datas
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleCancelEditDueDates}
                disabled={savingDueDates}
                className="flex items-center gap-2 text-gray-600 hover:bg-gray-100 px-3 py-2 rounded-md transition-colors"
              >
                <X size={18} />
                Cancelar
              </button>
              <button
                onClick={handleSaveDueDates}
                disabled={savingDueDates}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {savingDueDates ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Check size={18} />
                )}
                Salvar
              </button>
            </div>
          )}
        </div>
        
        {contract.due_dates && contract.due_dates.length > 0 ? (
          <div className="space-y-3">
            {contract.due_dates.map((date, index) => {
              const paymentStatus = getInstallmentPaymentStatus(index);
              const statusConfig = getPaymentStatusConfig(paymentStatus);
              const StatusIcon = statusConfig.icon;
              const isUpdating = updatingInstallment === index;
              
              return (
                <div 
                  key={index} 
                  className={`flex items-center justify-between p-4 rounded-lg border-2 ${statusConfig.bgColor} ${statusConfig.color.split(' ')[2]}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      paymentStatus === 'paid' ? 'bg-green-500 text-white' :
                      paymentStatus === 'overdue' ? 'bg-red-500 text-white' :
                      'bg-yellow-500 text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Parcela {index + 1}</p>
                      <p className="text-sm text-gray-600">
                        R$ {contract.installment_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      {editingDueDates ? (
                        <input
                          type="date"
                          value={editedDueDates[index] || ''}
                          onChange={(e) => handleDueDateChange(index, e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                        />
                      ) : (
                        <>
                          <p className="font-semibold text-gray-900">
                            {formatDateBR(date)}
                          </p>
                          <div className={`flex items-center gap-1 text-sm ${statusConfig.color.split(' ')[1]}`}>
                            <StatusIcon size={14} />
                            <span>{statusConfig.label}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Payment Action Buttons - hide when editing due dates or when cancelled */}
                    {!editingDueDates && paymentStatus !== 'cancelled' && (
                      <div className="flex gap-2">
                        {paymentStatus !== 'paid' && (
                          <button
                            onClick={() => handleInstallmentPayment(index, 'paid')}
                            disabled={isUpdating}
                            className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                            title="Marcar como Pago"
                          >
                            {isUpdating ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <Check size={16} />
                                Pago
                              </>
                            )}
                          </button>
                        )}
                        {paymentStatus === 'paid' && (
                          <button
                            onClick={() => handleInstallmentPayment(index, 'pending')}
                            disabled={isUpdating}
                            className="flex items-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors disabled:opacity-50 text-sm"
                            title="Marcar como Pendente"
                          >
                            {isUpdating ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <Clock size={16} />
                                Pendente
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Boleto Section */}
                  {!editingDueDates && (
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Receipt size={16} />
                        <span>Boleto:</span>
                        {contract.installment_boletos?.[index] ? (
                          <span className="text-green-600 font-medium">
                            {contract.installment_boletos[index].original_name}
                          </span>
                        ) : (
                          <span className="text-gray-400">Não anexado</span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {contract.installment_boletos?.[index] ? (
                          <>
                            <button
                              onClick={() => handleBoletoDownload(index)}
                              className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="Baixar Boleto"
                            >
                              <Download size={14} />
                              Baixar
                            </button>
                            <button
                              onClick={() => handleBoletoDelete(index)}
                              className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Remover Boleto"
                            >
                              <Trash2 size={14} />
                              Remover
                            </button>
                          </>
                        ) : null}
                        
                        <label className="flex items-center gap-1 px-2 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-md transition-colors cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            ref={el => boletoInputRefs.current[index] = el}
                            onChange={(e) => handleBoletoUpload(index, e.target.files[0])}
                            disabled={uploadingBoleto === index}
                          />
                          {uploadingBoleto === index ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                          ) : (
                            <>
                              <Upload size={14} />
                              {contract.installment_boletos?.[index] ? 'Substituir' : 'Anexar'}
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Nenhuma data de vencimento definida</p>
        )}
      </div>

      {/* Status Info */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Status do Contrato</h2>
        <div className="flex items-center gap-4">
          {getStatusBadge(contract.status)}
          <div>
            <p className="text-gray-600">{getStatusConfig(contract.status).description}</p>
            <p className="text-sm text-gray-500 mt-1">
              Última atualização: {new Date(contract.updated_at || contract.created_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Contract Documents */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-arcano-blue" />
            Documentos do Contrato ({contractDocuments.length})
          </h2>
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="flex items-center gap-2 px-4 py-2 bg-arcano-blue text-white rounded-md hover:bg-arcano-blue-dark transition-colors"
          >
            <Upload size={18} />
            Anexar Documento
          </button>
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-700">Enviar novo documento</h4>
              <button
                onClick={() => setShowUploadForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            {uploadMessage.text && (
              <div className={`mb-3 flex items-center gap-2 p-3 rounded-md ${
                uploadMessage.type === 'success' 
                  ? 'bg-green-50 text-green-700' 
                  : 'bg-red-50 text-red-700'
              }`}>
                {uploadMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                {uploadMessage.text}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <input
                  type="text"
                  value={docDescription}
                  onChange={(e) => setDocDescription(e.target.value)}
                  placeholder="Ex: Aditivo contratual, Comprovante..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arquivo
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-arcano-blue file:text-white hover:file:bg-arcano-blue-dark"
                />
              </div>
              {uploading && (
                <div className="flex items-center gap-2 text-arcano-blue">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-arcano-blue"></div>
                  Enviando...
                </div>
              )}
            </div>
          </div>
        )}

        {contractDocuments.length === 0 ? (
          <p className="text-gray-500 text-center py-4">Nenhum documento anexado a este contrato</p>
        ) : (
          <div className="space-y-2">
            {contractDocuments.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center gap-3">
                  <FileText size={20} className="text-gray-500" />
                  <div>
                    <p className="font-medium">{doc.filename}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</span>
                      {doc.file_size && <span>• {formatFileSize(doc.file_size)}</span>}
                      {doc.description && <span>• {doc.description}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDocumentDownload(doc)}
                    className="p-2 text-arcano-blue hover:bg-blue-50 rounded-md transition-colors"
                    title="Baixar"
                  >
                    <Download size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    title="Excluir"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDetail;
